import { Color } from 'three';
import type { DataSourceConfig } from '@dtwin/shared';

export interface SpikeChart3D {
  id: string;
  name: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  rows: number;
  cols: number;
  spacing: number;
  barSize: number;
  maxHeight: number;
  seed: number;
}

export type SpikeBoardChartType = 'line-chart' | 'bar-chart' | 'pie-chart' | 'scatter-3d' | 'surface-3d';
export type SpikeBoardResolution = 512 | 1024 | 2048;

export interface SpikeBoard3D {
  id: string;
  name: string;
  chartType: SpikeBoardChartType;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  width: number;
  height: number;
  resolution: SpikeBoardResolution;
  billboard: boolean;
  updateHz: number;
  opacity: number;
  background: string;
}

export const SPIKE_BOARD3D_DEFAULTS: Omit<SpikeBoard3D, 'id'> = {
  name: 'S0-B 图表贴屏',
  chartType: 'line-chart',
  position: [0, 5, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  width: 10,
  height: 6,
  resolution: 1024,
  billboard: true,
  updateHz: 2,
  opacity: 1,
  background: '#07131f',
};

export function createSpikeBoard3D(overrides: Partial<SpikeBoard3D> = {}): SpikeBoard3D {
  return {
    ...SPIKE_BOARD3D_DEFAULTS,
    ...overrides,
    id: overrides.id || `spike-board-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };
}

export interface SpikeHud3D {
  id: string;
  name: string;
  position: [number, number, number];
  title: string;
  content: string;
  width: number;
  background: string;
  visible: boolean;
}

export const SPIKE_HUD3D_DEFAULTS: Omit<SpikeHud3D, 'id'> = {
  name: 'S0-C HUD',
  position: [7, 5, 0],
  title: 'S0-C HTML HUD',
  content: 'HTML 叠加层：清晰、可交互、无深度遮挡',
  width: 300,
  background: 'rgba(4, 12, 24, 0.88)',
  visible: true,
};

export function createSpikeHud3D(overrides: Partial<SpikeHud3D> = {}): SpikeHud3D {
  return {
    ...SPIKE_HUD3D_DEFAULTS,
    ...overrides,
    id: overrides.id || `spike-hud-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };
}

export const SPIKE_CHART3D_DEFAULTS: Omit<SpikeChart3D, 'id'> = {
  name: 'S0-A 3D 柱状图',
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  rows: 40,
  cols: 50,
  spacing: 0.6,
  barSize: 0.42,
  maxHeight: 8,
  seed: 20261025,
};

export function createSpikeChart3D(overrides: Partial<SpikeChart3D> = {}): SpikeChart3D {
  return {
    ...SPIKE_CHART3D_DEFAULTS,
    ...overrides,
    id: overrides.id || `spike-chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };
}

function hash01(index: number, seed: number): number {
  let x = (index + 1) * 374761393 + seed * 668265263;
  x = (x ^ (x >>> 13)) * 1274126177;
  return ((x ^ (x >>> 16)) >>> 0) / 4294967295;
}

export function spikeBarValue(index: number, seed: number): number {
  return Math.round((10 + hash01(index, seed) * 90) * 10) / 10;
}

export function spikeBarHeight(value: number, maxHeight = SPIKE_CHART3D_DEFAULTS.maxHeight): number {
  return Math.max(0.15, (Math.max(0, Math.min(100, value)) / 100) * maxHeight);
}

export function spikeBarLocalPosition(
  index: number,
  chart: Pick<SpikeChart3D, 'rows' | 'cols' | 'spacing' | 'maxHeight' | 'seed'>,
): [number, number, number] {
  const row = Math.floor(index / chart.cols);
  const col = index % chart.cols;
  const x = (col - (chart.cols - 1) / 2) * chart.spacing;
  const z = (row - (chart.rows - 1) / 2) * chart.spacing;
  const height = spikeBarHeight(spikeBarValue(index, chart.seed), chart.maxHeight);
  return [x, height / 2, z];
}

export function spikeBarLabel(index: number, cols: number): string {
  const row = Math.floor(index / cols);
  const col = index % cols;
  return `产线${String(col + 1).padStart(2, '0')} / 区域${String(row + 1).padStart(2, '0')}`;
}

export function spikeBarColor(value: number): string {
  const t = Math.max(0, Math.min(1, value / 100));
  const hue = 0.58 - 0.45 * t;
  const lightness = 0.42 + 0.18 * t;
  return `#${new Color().setHSL(hue, 0.85, lightness).getHexString()}`;
}

export function spikeAxisTickIndices(count: number, maxTicks = 8): number[] {
  if (count <= 0) return [];
  const ticks = Math.max(2, Math.min(maxTicks, count));
  if (count <= ticks) return Array.from({ length: count }, (_, i) => i);
  const out = new Set<number>();
  for (let i = 0; i < ticks; i += 1) {
    out.add(Math.round((i * (count - 1)) / (ticks - 1)));
  }
  return [...out].sort((a, b) => a - b);
}

export function spikeAxisLabels(chart: SpikeChart3D): {
  xTicks: number[];
  zTicks: number[];
  x: string[];
  z: string[];
  xTitle: string;
  zTitle: string;
  yTitle: string;
} {
  const xTicks = spikeAxisTickIndices(chart.cols);
  const zTicks = spikeAxisTickIndices(chart.rows);
  return {
    xTicks,
    zTicks,
    x: xTicks.map((i) => `产线${String(i + 1).padStart(2, '0')}`),
    z: zTicks.map((i) => `区域${String(i + 1).padStart(2, '0')}`),
    xTitle: '产线（X）',
    zTitle: '区域（Z）',
    yTitle: '产量 / 万元',
  };
}

export function spikeChartBounds(chart: SpikeChart3D): {
  center: [number, number, number];
  size: [number, number, number];
  min: [number, number, number];
  max: [number, number, number];
} {
  const halfX = ((chart.cols - 1) / 2) * chart.spacing + chart.barSize / 2;
  const halfZ = ((chart.rows - 1) / 2) * chart.spacing + chart.barSize / 2;
  const height = chart.maxHeight;
  return {
    center: [0, height / 2, 0],
    size: [halfX * 2, height, halfZ * 2],
    min: [-halfX, 0, -halfZ],
    max: [halfX, height, halfZ],
  };
}

export type Chart3DKind =
  | 'bar3d'
  | 'scatter3d'
  | 'surface3d'
  | 'line3d'
  | 'pie3d'
  | 'donut3d'
  | 'gauge3d'
  | 'radar3d'
  | 'funnel3d'
  | 'board3d'
  | 'map3d'
  | 'globe3d'
  | 'graph3d';

export interface Chart3DBarStyle {
  cols: number;
  spacing: number;
  barSize: number;
  maxHeight: number;
  palette: string[];
  opacity: number;
  metalness: number;
  roughness: number;
  emissive: string;
  emissiveIntensity: number;
  valueLabels: boolean;
}

export interface Chart3DScatterStyle {
  pointSize: number;
  spread: number;
  opacity: number;
  palette: string[];
}

export interface Chart3DSurfaceStyle {
  gridSize: number;
  maxHeight: number;
  spread: number;
  opacity: number;
  wireframe: boolean;
  palette: string[];
  metalness: number;
  roughness: number;
}

export interface Chart3DLineStyle {
  lineWidth: number;
  maxHeight: number;
  spread: number;
  smooth: boolean;
  color: string;
  showPoints: boolean;
  pointSize: number;
}

export interface Chart3DPieStyle {
  radius: number;
  innerRadius: number;
  height: number;
  gap: number;
  opacity: number;
  palette: string[];
}

export interface Chart3DGaugeStyle {
  radius: number;
  thickness: number;
  min: number;
  max: number;
  value: number;
  startAngle: number;
  endAngle: number;
  color: string;
  trackColor: string;
}

export interface Chart3DRadarStyle {
  radius: number;
  levels: number;
  fillOpacity: number;
  color: string;
}

export interface Chart3DFunnelStyle {
  height: number;
  topWidth: number;
  gap: number;
  opacity: number;
  palette: string[];
}

export interface Chart3DMapStyle {
  geoUrl: string;
  scale: number;
  extrude: number;
  color: string;
  opacity: number;
  showBorders: boolean;
}

export interface Chart3DGlobeStyle {
  radius: number;
  textureUrl: string;
  autoRotate: boolean;
  rotateSpeed: number;
  atmosphere: boolean;
  pointCount: number;
  pointColor: string;
}

export interface Chart3DGraphStyle {
  nodeSize: number;
  linkWidth: number;
  layout: 'sphere' | 'grid';
  nodeColor: string;
  linkColor: string;
}

export interface Chart3DBoardStyle {
  chartType: SpikeBoardChartType;
  width: number;
  height: number;
  resolution: SpikeBoardResolution;
  billboard: boolean;
  updateHz: number;
  opacity: number;
  background: string;
}

export interface Chart3DAxesStyle {
  show: boolean;
  xLabel: string;
  yLabel: string;
  zLabel: string;
  color: string;
  grid: boolean;
}

export interface Chart3DLegendStyle {
  show: boolean;
}

export interface Chart3DLabelStyle {
  show: boolean;
  max: number;
}

export interface Chart3DStatusRule {
  metric: 'value';
  op: '>' | '>=' | '<' | '<=' | '=';
  threshold: number;
  color: string;
}

export interface Chart3DAppearance {
  theme: 'dark' | 'light' | 'tech';
  accent: string;
  outline: boolean;
  outlineColor: string;
  statusRules: Chart3DStatusRule[];
}

export interface Chart3DAnimation {
  enabled: boolean;
  grow: boolean;
  durationMs: number;
  stagger: number;
  loop: boolean;
  spinSpeed: number;
}

export interface Chart3DObject {
  id: string;
  name: string;
  kind: Chart3DKind;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  visible: boolean;
  locked?: boolean;
  perfTier?: 'high' | 'medium' | 'low';
  dataSource?: DataSourceConfig;
  dataMapping?: { category?: string; value?: string; series?: string; x?: string; y?: string; z?: string };
  bar: Chart3DBarStyle;
  scatter: Chart3DScatterStyle;
  surface: Chart3DSurfaceStyle;
  line: Chart3DLineStyle;
  pie: Chart3DPieStyle;
  gauge: Chart3DGaugeStyle;
  radar: Chart3DRadarStyle;
  funnel: Chart3DFunnelStyle;
  board: Chart3DBoardStyle;
  map: Chart3DMapStyle;
  globe: Chart3DGlobeStyle;
  graph: Chart3DGraphStyle;
  axes: Chart3DAxesStyle;
  legend: Chart3DLegendStyle;
  labels: Chart3DLabelStyle;
  appearance: Chart3DAppearance;
  animation: Chart3DAnimation;
  demo: { count: number; seed: number };
}

export const CHART3D_DEFAULTS: Omit<Chart3DObject, 'id'> = {
  name: '3D 柱状图',
  kind: 'bar3d',
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  visible: true,
  perfTier: 'high',
  bar: {
    cols: 20,
    spacing: 0.8,
    barSize: 0.55,
    maxHeight: 6,
    palette: ['#0b5cff', '#00d8ff', '#ffd666'],
    opacity: 1,
    metalness: 0.25,
    roughness: 0.55,
    emissive: '#000000',
    emissiveIntensity: 0,
    valueLabels: false,
  },
  scatter: {
    pointSize: 0.28,
    spread: 12,
    opacity: 0.95,
    palette: ['#0b5cff', '#00d8ff', '#ffd666'],
  },
  surface: {
    gridSize: 24,
    maxHeight: 6,
    spread: 12,
    opacity: 0.95,
    wireframe: false,
    palette: ['#0b3d91', '#00d8ff', '#ffd666'],
    metalness: 0.15,
    roughness: 0.65,
  },
  line: {
    lineWidth: 2.5,
    maxHeight: 6,
    spread: 12,
    smooth: true,
    color: '#00d8ff',
    showPoints: true,
    pointSize: 0.18,
  },
  pie: {
    radius: 4.5,
    innerRadius: 0,
    height: 1.2,
    gap: 0.03,
    opacity: 1,
    palette: ['#0b5cff', '#00d8ff', '#ffd666', '#7c5cff', '#ff7a45'],
  },
  gauge: {
    radius: 4.2,
    thickness: 0.85,
    min: 0,
    max: 100,
    value: 68,
    startAngle: -220,
    endAngle: 40,
    color: '#00d8ff',
    trackColor: '#16344d',
  },
  radar: {
    radius: 4.5,
    levels: 4,
    fillOpacity: 0.28,
    color: '#00d8ff',
  },
  funnel: {
    height: 1.1,
    topWidth: 7,
    gap: 0.14,
    opacity: 0.95,
    palette: ['#0b5cff', '#00d8ff', '#ffd666', '#7c5cff', '#ff7a45'],
  },
  board: {
    chartType: 'line-chart',
    width: 10,
    height: 6,
    resolution: 1024,
    billboard: false,
    updateHz: 2,
    opacity: 1,
    background: '#07131f',
  },
  map: {
    geoUrl: '/assets/geo/china.json',
    scale: 20,
    extrude: 0.8,
    color: '#0b5cff',
    opacity: 0.95,
    showBorders: true,
  },
  globe: {
    radius: 5,
    textureUrl: '/assets/globe/earth.jpg',
    autoRotate: true,
    rotateSpeed: 0.08,
    atmosphere: true,
    pointCount: 24,
    pointColor: '#ffd666',
  },
  graph: {
    nodeSize: 0.35,
    linkWidth: 1.2,
    layout: 'sphere',
    nodeColor: '#00d8ff',
    linkColor: '#5a8fb0',
  },
  axes: {
    show: true,
    xLabel: 'X',
    yLabel: '数值',
    zLabel: 'Z',
    color: '#5a8fb0',
    grid: false,
  },
  legend: { show: true },
  labels: { show: false, max: 20 },
  appearance: {
    theme: 'tech',
    accent: '#00d8ff',
    outline: true,
    outlineColor: '#ffd666',
    statusRules: [],
  },
  animation: { enabled: true, grow: true, durationMs: 900, stagger: 0, loop: false, spinSpeed: 0 },
  demo: { count: 200, seed: 20261026 },
};

export function createChart3DObject(overrides: Partial<Chart3DObject> = {}): Chart3DObject {
  return {
    ...CHART3D_DEFAULTS,
    ...overrides,
    bar: { ...CHART3D_DEFAULTS.bar, ...(overrides.bar || {}) },
    scatter: { ...CHART3D_DEFAULTS.scatter, ...(overrides.scatter || {}) },
    surface: { ...CHART3D_DEFAULTS.surface, ...(overrides.surface || {}) },
    line: { ...CHART3D_DEFAULTS.line, ...(overrides.line || {}) },
    pie: { ...CHART3D_DEFAULTS.pie, ...(overrides.pie || {}) },
    gauge: { ...CHART3D_DEFAULTS.gauge, ...(overrides.gauge || {}) },
    radar: { ...CHART3D_DEFAULTS.radar, ...(overrides.radar || {}) },
    funnel: { ...CHART3D_DEFAULTS.funnel, ...(overrides.funnel || {}) },
    board: { ...CHART3D_DEFAULTS.board, ...(overrides.board || {}) },
    map: { ...CHART3D_DEFAULTS.map, ...(overrides.map || {}) },
    globe: { ...CHART3D_DEFAULTS.globe, ...(overrides.globe || {}) },
    graph: { ...CHART3D_DEFAULTS.graph, ...(overrides.graph || {}) },
    axes: { ...CHART3D_DEFAULTS.axes, ...(overrides.axes || {}) },
    legend: { ...CHART3D_DEFAULTS.legend, ...(overrides.legend || {}) },
    labels: { ...CHART3D_DEFAULTS.labels, ...(overrides.labels || {}) },
    appearance: {
      ...CHART3D_DEFAULTS.appearance,
      ...(overrides.appearance || {}),
      statusRules: overrides.appearance?.statusRules ?? CHART3D_DEFAULTS.appearance.statusRules,
    },
    animation: { ...CHART3D_DEFAULTS.animation, ...(overrides.animation || {}) },
    demo: { ...CHART3D_DEFAULTS.demo, ...(overrides.demo || {}) },
    id: overrides.id || `chart3d-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };
}
